import _ from 'lodash';

import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { fToNow, fDateTime } from 'src/utils/format-time';

import { IContactListItem } from 'src/services/contacts/contacts.types';

import Label from 'src/components/label';
import { ColumnDef } from 'src/components/data-list/data-list.types';

// ----------------------------------------------------------------------

type Translate = (key: string, options?: any) => string;

const dash = <Typography variant="body2" color="textSecondary">--</Typography>;

const MAX_LISTS = 2;
const MAX_LABELS = 2;

interface BuildContactColumnsArgs {
  t: Translate;
  /** Opens the record panel overlay (JT1-1529). The name becomes a button that opens it, not a hyperlink. */
  onOpenRecord: (contactId: string) => void;
}

/**
 * Contacts list column registry (JT1-1528, item 5). Linked company, role, email, phone, location,
 * PSA, list memberships (truncated "+N"), owner and last-updated. Location (item 30) and PSA (item 1)
 * were wired in JT1-1541 — both default-hidden so saved layouts are undisturbed.
 */
export function buildContactColumns({ t, onOpenRecord }: BuildContactColumnsArgs): ColumnDef<IContactListItem>[] {
  return [
    {
      key: 'name',
      label: t('contacts.table.name', { defaultValue: 'Name' }),
      fixed: true,
      sortable: true,
      searchable: true,
      minWidth: 200,
      render: (row) => (
        <Stack direction="column">
          {/* Name opens the context-preserving record overlay (JT1-1529) — no longer a hyperlink. */}
          <Link component="button" type="button" onClick={() => onOpenRecord(row.id)} color="inherit" underline="hover" noWrap sx={{ textTransform: 'capitalize', textAlign: 'left' }}>
            <Typography variant="subtitle2">
              {_.capitalize(`${row.first_name ?? ''} ${row.last_name ?? ''}`.trim()) || '--'}
            </Typography>
          </Link>
          {row.position_title ? (
            <Typography variant="caption" color="text.secondary">{row.position_title}</Typography>
          ) : null}
        </Stack>
      ),
    },
    {
      key: 'company',
      label: t('contacts.table.company', { defaultValue: 'Linked company' }),
      defaultVisible: true,
      searchable: true,
      render: (row) => (row.company_name ? <Typography variant="body2" color="text.secondary">{row.company_name}</Typography> : dash),
    },
    {
      key: 'email',
      label: t('contacts.table.email', { defaultValue: 'Email' }),
      defaultVisible: true,
      searchable: true,
      minWidth: 180,
      render: (row) => (row.email ? <Link variant="body2" underline="hover" color="InfoText" href={`mailto:${row.email}`}>{row.email}</Link> : dash),
    },
    {
      key: 'phone',
      label: t('contacts.table.phone', { defaultValue: 'Phone' }),
      defaultVisible: true,
      render: (row) => (row.phone ? <Link variant="body2" underline="hover" color="InfoText" href={`tel:${row.phone}`}>{row.phone}</Link> : dash),
    },
    {
      key: 'location',
      label: t('contacts.table.location', { defaultValue: 'Location' }),
      defaultVisible: false,
      sortable: true,
      searchable: true,
      render: (row) => (row.location ? <Typography variant="body2" color="text.secondary">{row.location}</Typography> : dash),
    },
    {
      key: 'labels',
      label: t('contacts.table.labels', { defaultValue: 'Labels' }),
      defaultVisible: true,
      searchable: true,
      // Labels inherited from the linked company (JT1-1541 QA — generalised from the PSA boolean so any
      // label shows and is searchable). Contacts have no labels of their own.
      render: (row) => {
        if (!row.labels?.length) return dash;
        const shown = row.labels.slice(0, MAX_LABELS);
        const extra = row.labels.length - shown.length;
        return (
          <Tooltip title={row.labels.join(', ')}>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
              {shown.map((label) => (
                <Label key={label} color="info" variant="soft">{label}</Label>
              ))}
              {extra > 0 && <Typography variant="caption" color="text.secondary">+{extra}</Typography>}
            </Stack>
          </Tooltip>
        );
      },
    },
    {
      key: 'lists',
      label: t('contacts.table.lists', { defaultValue: 'Lists' }),
      defaultVisible: true,
      render: (row) => {
        if (!row.lists?.length) return dash;
        const shown = row.lists.slice(0, MAX_LISTS).join(', ');
        const extra = row.lists.length - MAX_LISTS;
        return (
          <Tooltip title={row.lists.join(', ')}>
            <Typography variant="body2" color="text.secondary">{shown}{extra > 0 ? ` +${extra}` : ''}</Typography>
          </Tooltip>
        );
      },
    },
    {
      key: 'account_owner',
      label: t('contacts.table.owner', { defaultValue: 'Owner' }),
      defaultVisible: false,
      render: (row) => (row.account_owner_name ? <Typography variant="body2" color="text.secondary">{row.account_owner_name}</Typography> : dash),
    },
    {
      key: 'last_activity_at',
      label: t('contacts.table.last_updated', { defaultValue: 'Last updated' }),
      defaultVisible: true,
      sortable: true,
      render: (row) =>
        row.last_activity_at ? (
          <Tooltip title={fToNow(row.last_activity_at)}>
            <Typography variant="body2" color="text.secondary">{fDateTime(row.last_activity_at)}</Typography>
          </Tooltip>
        ) : dash,
    },
  ];
}
